import * as THREE from 'three';
import { IntersectionData } from './intersectionTypes';
import { Model } from '../hooks/useModelState';
import { threeJsPositionToCad } from '../Scenes/scene-utils';
import PCSpool from './Spools/PCSpool';

interface ProcessIntersectionsParams {
  intersections: THREE.Intersection[];
  model: Model;
  offset: THREE.Vector3;
  scale: number;
  camera?: THREE.Camera;
  pointer?: { x: number; y: number };
}

const pointToSegmentDistanceSq = (
  px: number, py: number,
  ax: number, ay: number,
  bx: number, by: number,
): number => {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return (px - ax) ** 2 + (py - ay) ** 2;
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
  const closestX = ax + t * dx;
  const closestY = ay + t * dy;
  return (px - closestX) ** 2 + (py - closestY) ** 2;
};

const getSpoolScreenDistance = (
  spool: PCSpool,
  offset: THREE.Vector3,
  scale: number,
  camera: THREE.Camera,
  pointer: { x: number; y: number },
): number => {
  if (!spool.pointA || !spool.pointB) return Infinity;
  const startA = new THREE.Vector3(
    (-spool.pointA.coords.x + offset.x) * scale,
    (spool.pointA.coords.z + offset.y) * scale,
    (spool.pointA.coords.y + offset.z) * scale,
  );
  const endB = new THREE.Vector3(
    (-spool.pointB.coords.x + offset.x) * scale,
    (spool.pointB.coords.z + offset.y) * scale,
    (spool.pointB.coords.y + offset.z) * scale,
  );
  const projA = startA.project(camera);
  const projB = endB.project(camera);
  return pointToSegmentDistanceSq(pointer.x, pointer.y, projA.x, projA.y, projB.x, projB.y);
};

export const getNodeScreenDistance = (
  node: { coords: { x: number; y: number; z: number } },
  offset: THREE.Vector3,
  scale: number,
  camera: THREE.Camera,
  pointer: { x: number; y: number },
): number => {
  const pos = new THREE.Vector3(
    (-node.coords.x + offset.x) * scale,
    (node.coords.z + offset.y) * scale,
    (node.coords.y + offset.z) * scale,
  );
  const proj = pos.project(camera);
  return (pointer.x - proj.x) ** 2 + (pointer.y - proj.y) ** 2;
};

const calculateSpoolClickPoint = (
    point: THREE.Vector3,
    spool: PCSpool,
    offset: THREE.Vector3,
    scale: number,
): { x: number; y: number; z: number } | undefined => {
    if (!spool.pointA || !spool.pointB) return undefined;
    const startThreeJs = new THREE.Vector3(
      (-spool.pointA.coords.x + offset.x) * scale,
      (spool.pointA.coords.z + offset.y) * scale,
      (spool.pointA.coords.y + offset.z) * scale,
    );
    const endThreeJs = new THREE.Vector3(
      (-spool.pointB.coords.x + offset.x) * scale,
      (spool.pointB.coords.z + offset.y) * scale,
      (spool.pointB.coords.y + offset.z) * scale,
    );
    const lineDirection = new THREE.Vector3().subVectors(endThreeJs, startThreeJs).normalize();
    const toClickPoint = new THREE.Vector3().subVectors(point, startThreeJs);
    const projectionLength = toClickPoint.dot(lineDirection);
    const clampedLength = Math.max(0, Math.min(projectionLength, startThreeJs.distanceTo(endThreeJs)));
    const closestPointOnLineThreeJs = new THREE.Vector3().copy(startThreeJs).add(lineDirection.multiplyScalar(clampedLength));
    const cadCoords = threeJsPositionToCad(closestPointOnLineThreeJs, offset, scale);
    return { x: cadCoords.x, y: cadCoords.y, z: cadCoords.z };
  };

const processIntersections = ({
  intersections,
  model,
  offset,
  scale,
  camera,
  pointer,
}: ProcessIntersectionsParams): IntersectionData[] => {
  const hasScreenData = !!camera && !!pointer;

  const intersectionData = intersections
    .map((intersection: THREE.Intersection): IntersectionData | null => {
      const obj = intersection.object;
      const nodeId = obj.userData?.instancedNodes && intersection.instanceId != null
        ? obj.userData.nodeIdByIndex?.[intersection.instanceId]
        : obj.userData?.nodeId;
      const mergedSpoolIds = obj.userData?.mergedSpoolIds as string[] | undefined;
      const spoolId = mergedSpoolIds && intersection.index != null
        ? mergedSpoolIds[Math.floor(intersection.index / 2)]
        : obj.userData?.spoolId;

      if (nodeId && model.nodes[nodeId]) {
        const screenDistance = hasScreenData
          ? getNodeScreenDistance(model.nodes[nodeId], offset, scale, camera, pointer)
          : undefined;
        return {
          type: 'node' as const,
          node: model.nodes[nodeId],
          nodeId,
          distance: intersection.distance,
          screenDistance,
        };
      }
      if (spoolId && model.spools[spoolId]) {
        const spool = model.spools[spoolId];
        let clickPoint: { x: number; y: number; z: number } | undefined;
        if (intersection.point) {
          clickPoint = calculateSpoolClickPoint(intersection.point, spool, offset, scale);
        }
        const screenDistance = hasScreenData
          ? getSpoolScreenDistance(spool, offset, scale, camera, pointer)
          : undefined;
        return {
          type: 'spool' as const,
          spool,
          spoolId,
          clickPoint,
          distance: intersection.distance,
          screenDistance,
        };
      }
      return null;
    })
    .filter((item): item is IntersectionData => item !== null);

  intersectionData.sort((a: IntersectionData, b: IntersectionData) => {
    if (a.screenDistance !== undefined && b.screenDistance !== undefined) {
      return a.screenDistance - b.screenDistance;
    }
    return a.distance - b.distance;
  });

  return intersectionData;
};

export default processIntersections;
